/**
 * <s-faq-hub> — FAQ help hub (port of the live theme's FAQ page enhancements).
 *
 * The FAQ questions, answers, filters and guide links live in the FAQs page's
 * own content (edited in Online Store > Pages), so new entries added there are
 * picked up automatically. This component adds:
 *   - live search, filters (type, category, topic, collection, brand), sorting,
 *     category chips and "show more" over every [data-entry] in #ab-faq-hub;
 *   - a "Related articles from our blogs" panel that searches every published
 *     blog article through the search.ab-faq-articles view.
 * If anything fails, all original FAQ content stays visible.
 */
function initFaqControls(host) {
    const root = host.querySelector('#ab-faq-hub');
    if (!root || root.dataset.controlsReady === 'true') return;
    const get = id => root.querySelector('#' + id);
    const search = get('ab-search');
    const results = get('ab-results');
    const status = get('ab-status');
    const empty = get('ab-empty');
    const active = get('ab-active');
    const more = get('ab-more');
    const clear = get('ab-search-clear');
    const sort = get('ab-sort');
    const toggle = get('ab-filter-toggle');
    const filterKeys = ['kind', 'category', 'topic', 'collection', 'brand'];
    const controls = Object.fromEntries(filterKeys.map(key => [key, get('ab-' + key)]));
    if (!search || !results || !status || !empty || !sort || filterKeys.some(k => !controls[k])) return;
    const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const list = value => String(value || '').split(/[,|]/).map(x => x.trim()).filter(Boolean);
    const groups = Array.from(results.querySelectorAll('.ab-group'));
    const groupOrder = new Map(groups.map((g, i) => [g, i]));
    const records = Array.from(results.querySelectorAll('[data-entry]')).map((node, index) => {
      const group = node.closest('.ab-group');
      const title = node.querySelector('h3');
      const category = node.dataset.category || (group && group.dataset.group) || 'Other';
      const collections = list(node.dataset.collections || node.dataset.collection || (group && group.dataset.collection));
      const brands = list(node.dataset.brands || node.dataset.brand);
      const topics = list(node.dataset.topics || node.dataset.topic);
      const heading = title ? title.textContent.trim() : node.textContent.trim();
      return { node, group, index, category, collections, brands, topics, heading,
        kind: node.dataset.kind || 'faq',
        title: normalize(heading),
        text: normalize([node.textContent, category, collections.join(' '), brands.join(' '), topics.join(' '), node.dataset.keywords || ''].join(' ')) };
    });
    if (!records.length) return;
    const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
    let visibleLimit = 24;
    let timer;
    const fillOptions = (control, values) => {
      const existingLabels = new Map(Array.from(control.options).map(o => [o.value, o.textContent]));
      const placeholder = control.options[0] ? control.options[0].textContent : 'All';
      control.replaceChildren(new Option(placeholder, ''));
      [...new Set(values)].sort(collator.compare).forEach(value => {
        control.add(new Option(existingLabels.get(value) || value.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), value));
      });
    };
    const failSafe = error => {
      root.classList.remove('ab-ready');
      root.dataset.controlsReady = 'false';
      records.forEach(r => { r.node.hidden = false; });
      groups.forEach(g => { g.hidden = false; });
      empty.hidden = true;
      if (active) active.hidden = true;
      status.textContent = 'Browse quick answers and existing beauty guides.';
      console.error('[Aslam Beauty FAQ hub]', error);
    };
    try {
      fillOptions(controls.category, records.map(r => r.category));
      fillOptions(controls.topic, records.flatMap(r => r.topics));
      fillOptions(controls.collection, records.flatMap(r => r.collections));
      fillOptions(controls.brand, records.flatMap(r => r.brands));
      sort.replaceChildren(new Option('Relevance / original order', 'recommended'), new Option('Category / A–Z', 'alphabetical'));
      search.setAttribute('aria-controls', 'ab-results');
      search.setAttribute('aria-describedby', 'ab-search-hint');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      if (toggle) {
        toggle.setAttribute('aria-controls', 'ab-filters');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.textContent = 'Show filters';
      }
      const hint = get('ab-search-hint');
      if (hint) hint.textContent = 'Search questions, answers, guide titles and topics. Try cash on delivery, dandruff or sunscreen.';
      const score = (record, query, tokens) => {
        if (!query) return 0;
        if (!tokens.every(token => record.text.includes(token))) return -1;
        return (record.title === query ? 120 : 0) + (record.title.includes(query) ? 60 : 0) + tokens.reduce((n, token) => n + (record.title.includes(token) ? 12 : 1), 0);
      };
      const render = () => {
        try {
          const query = normalize(search.value);
          const tokens = query.split(' ').filter(Boolean);
          const state = Object.fromEntries(filterKeys.map(key => [key, controls[key].value]));
          const matched = records.map(r => ({ ...r, score: score(r, query, tokens) })).filter(r =>
            r.score >= 0 && (!state.kind || r.kind === state.kind) &&
            (!state.category || r.category === state.category) &&
            (!state.topic || r.topics.includes(state.topic)) &&
            (!state.collection || r.collections.includes(state.collection)) &&
            (!state.brand || r.brands.includes(state.brand))
          );
          const byGroup = new Map(groups.map(g => [g, []]));
          matched.forEach(r => { if (byGroup.has(r.group)) byGroup.get(r.group).push(r); });
          const alphabetical = sort.value === 'alphabetical';
          const orderedGroups = groups.slice().sort((a, b) => {
            if (alphabetical) return collator.compare(a.dataset.group || '', b.dataset.group || '');
            if (query) {
              const max = g => Math.max(-1, ...byGroup.get(g).map(r => r.score));
              const diff = max(b) - max(a);
              if (diff) return diff;
            }
            return groupOrder.get(a) - groupOrder.get(b);
          });
          records.forEach(r => { r.node.hidden = true; });
          let shown = 0;
          orderedGroups.forEach(group => {
            results.appendChild(group);
            const entries = byGroup.get(group).sort((a, b) => alphabetical ? collator.compare(a.heading, b.heading) : (query ? b.score - a.score || a.index - b.index : a.index - b.index));
            let inGroup = 0;
            entries.forEach(r => {
              group.appendChild(r.node);
              if (shown < visibleLimit) { r.node.hidden = false; shown++; inGroup++; }
            });
            group.hidden = inGroup === 0;
          });
          empty.hidden = matched.length !== 0;
          status.textContent = matched.length === 0 ? 'No matching answers or guides.' : 'Showing ' + shown + ' of ' + matched.length + ' matching answers and guides';
          if (active) {
            const labels = [];
            if (search.value.trim()) labels.push('Search: ' + search.value.trim());
            filterKeys.forEach(key => {
              if (state[key]) labels.push(controls[key].selectedOptions[0].textContent);
            });
            active.textContent = labels.join(' · ');
            active.hidden = labels.length === 0;
          }
          if (clear) clear.disabled = !search.value;
          if (more) {
            const remain = matched.length - shown;
            more.hidden = remain <= 0;
            if (more.parentElement) more.parentElement.hidden = remain <= 0;
            more.textContent = 'Show more results (' + remain + ' remaining)';
          }
          root.querySelectorAll('.ab-chips [data-category]').forEach(button => {
            button.setAttribute('aria-pressed', String(button.dataset.category === state.category));
          });
        } catch (error) { failSafe(error); }
      };
      const update = () => { clearTimeout(timer); visibleLimit = 24; render(); };
      const reset = () => {
        search.value = '';
        filterKeys.forEach(key => { controls[key].value = ''; });
        sort.value = 'recommended';
        update();
      };
      search.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(update, 100); });
      filterKeys.forEach(key => controls[key].addEventListener('change', update));
      sort.addEventListener('change', update);
      root.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !root.contains(button)) return;
        if (button === clear) { search.value = ''; update(); search.focus(); }
        else if (button.hasAttribute('data-reset')) { reset(); }
        else if (button === more) { clearTimeout(timer); visibleLimit += 24; render(); }
        else if (button === toggle) {
          const open = root.dataset.filtersOpen !== 'true';
          root.dataset.filtersOpen = String(open);
          toggle.setAttribute('aria-expanded', String(open));
          toggle.textContent = open ? 'Hide filters' : 'Show filters';
        } else if (button.hasAttribute('data-category')) {
          controls.category.value = button.dataset.category || '';
          update();
        }
      });
      root.dataset.controlsReady = 'true';
      root.classList.add('ab-ready');
      render();
    } catch (error) { failSafe(error); }
}

function initFaqArticles(host) {
    const root = host.querySelector('#ab-faq-hub');
    if (!root || root.dataset.nativeArticlesReady) return;
    const get = id => root.querySelector('#ab-' + id);
    const input = get('search'), results = get('results');
    const keys = ['kind', 'category', 'topic', 'collection', 'brand'];
    const controls = Object.fromEntries(keys.map(k => [k, get(k)]));
    if (!input || !results || keys.some(k => !controls[k]) || !get('sort')) return;
    root.dataset.nativeArticlesReady = 'true';
    const norm = x => String(x || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const stop = new Set('a an the is are am was were do does did can could should would will how what why when where which who i my me you your we our to for of in on at with and or it this that please'.split(' '));
    const terms = () => norm(input.value).split(' ').filter(t => t && !stop.has(t)).join(' ');
    const path = x => { try { const u = new URL(x, location.origin); return u.origin === location.origin && /\/blogs\/[^/]+\/[^/]+/.test(u.pathname) ? u.pathname.replace(/\/$/, '') : ''; } catch (_) { return ''; } };
    const split = x => String(x || '').split(/[,|]/).map(v => v.trim()).filter(Boolean);
    const known = new Map(), blogs = new Map();
    results.querySelectorAll('[data-kind="guide"]').forEach(node => {
      const a = node.querySelector('h3 a[href]'), p = a && path(a.getAttribute('href'));
      const g = node.closest('.ab-group');
      if (!p || !g) return;
      const meta = { category: node.dataset.category || g.dataset.group || 'Other beauty guides', collections: split(node.dataset.collections || node.dataset.collection || g.dataset.collection), topics: split(node.dataset.topics || node.dataset.topic), brands: split(node.dataset.brands || node.dataset.brand), node };
      known.set(p, meta); blogs.set(p.split('/blogs/')[1].split('/')[0], meta);
    });
    const panel = document.createElement('section');
    panel.className = 'ab-group'; panel.hidden = true;
    const heading = document.createElement('h2'); heading.textContent = 'Related articles from our blogs';
    const note = document.createElement('p'); note.setAttribute('role', 'status'); note.setAttribute('aria-live', 'polite');
    const cards = document.createElement('div');
    const more = document.createElement('button'); more.type = 'button'; more.textContent = 'Show more related articles';
    panel.append(heading, note, cards, more); results.after(panel);
    let timer, controller, generation = 0, articles = [], limit = 24, state = '', lastQuery = '';
    const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
    const refreshOptions = () => {
      const values = { category: articles.map(a => a.category), topic: articles.flatMap(a => a.topics), collection: articles.flatMap(a => a.collections), brand: articles.flatMap(a => a.brands) };
      Object.entries(values).forEach(([key, all]) => {
        const c = controls[key], existing = new Set(Array.from(c.options, o => o.value));
        all.forEach(v => { if (v && !existing.has(v)) { c.add(new Option(v.replace(/-/g, ' '), v)); existing.add(v); } });
      });
    };
    function render() {
      const q = terms();
      if (!q || controls.kind.value === 'faq') { panel.hidden = true; return; }
      panel.hidden = false;
      const selected = Object.fromEntries(keys.map(k => [k, controls[k].value]));
      const matched = articles.filter(a => (!selected.category || selected.category === a.category) && (!selected.topic || a.topics.includes(selected.topic)) && (!selected.collection || a.collections.includes(selected.collection)) && (!selected.brand || a.brands.includes(selected.brand)) && !(known.get(a.url) && !known.get(a.url).node.hidden));
      if (get('sort').value === 'alphabetical') matched.sort((a,b) => collator.compare(a.category,b.category) || collator.compare(a.title,b.title));
      cards.replaceChildren();
      matched.slice(0, limit).forEach(a => {
        const card = document.createElement('article'); card.className = 'ab-entry ab-guide';
        const h = document.createElement('h3'), link = document.createElement('a');
        link.href = a.url; link.textContent = a.title; h.append(link);
        const p = document.createElement('p'); p.textContent = a.excerpt;
        card.append(h,p); cards.append(card);
      });
      note.textContent = state === 'loading' ? 'Searching published blog content...' : state === 'error' ? 'Blog search is temporarily unavailable. Existing FAQ answers and guide links still work.' : matched.length ? 'Showing ' + Math.min(limit, matched.length) + ' of ' + matched.length + ' additional related articles.' : 'No additional articles for this search and these filters. Matching guide links may already appear above.';
      more.hidden = matched.length <= limit;
      if (matched.length && get('empty')) get('empty').hidden = true;
    }
    async function load(q, token) {
      controller = new AbortController(); const active = controller;
      state = 'loading'; articles = []; render();
      try {
        const found = new Map(); let page = 1; const visited = new Set();
        while (page) {
          if (visited.has(page)) throw new Error('Repeated pagination'); visited.add(page);
          const base = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
          const url = new URL(base + 'search', location.origin);
          url.searchParams.set('view','ab-faq-articles'); url.searchParams.set('type','article');
          url.searchParams.set('q',q); url.searchParams.set('options[prefix]','last'); url.searchParams.set('page',String(page));
          const preview = new URLSearchParams(location.search).get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id',preview);
          const timeout = setTimeout(() => active.abort(),12000);
          let data;
          try { const response = await fetch(url, { signal: active.signal, credentials: 'same-origin', cache: 'no-store' }); if (!response.ok) throw new Error('Search request failed'); data = await response.json(); } finally { clearTimeout(timeout); }
          if (token !== generation) return;
          if (data.version !== 1 || !Array.isArray(data.articles)) throw new Error('Unexpected search response');
          data.articles.forEach(a => {
            const p = path(a.url); if (!p || found.has(p) || typeof a.title !== 'string') return;
            const tags = Array.isArray(a.tags) ? a.tags : [];
            const meta = known.get(p), blog = blogs.get(p.split('/blogs/')[1].split('/')[0]);
            const tagged = prefix => tags.filter(t => typeof t === 'string' && t.toLowerCase().startsWith(prefix)).map(t => t.slice(prefix.length).trim()).filter(Boolean);
            const brands = tagged('faq-brand:');
            if (!brands.length) Array.from(controls.brand.options).forEach(o => { if (o.value && tags.some(t => norm(t) === norm(o.value))) brands.push(o.value); });
            found.set(p, { title: a.title, url: p, excerpt: typeof a.excerpt === 'string' ? a.excerpt : '', category: meta ? meta.category : blog ? blog.category : 'Other beauty guides', collections: meta ? meta.collections : blog ? blog.collections : tagged('faq-collection:'), topics: meta ? meta.topics : tagged('faq-topic:'), brands: meta ? meta.brands : brands });
          });
          page = Number.isInteger(data.next_page) && data.next_page > page ? data.next_page : null;
        }
        if (token !== generation) return;
        articles = Array.from(found.values()); state = 'done'; refreshOptions(); render();
      } catch (error) { if (token === generation) { state = 'error'; articles = []; render(); } }
    }
    function update() {
      const q = terms(); limit = 24;
      if (q !== lastQuery) {
        clearTimeout(timer);
        lastQuery = q; generation++; if (controller) controller.abort(); articles = []; state = '';
        const token = generation;
        if (q) { state = 'loading'; timer = setTimeout(() => load(q,token),350); }
      }
      render();
    }
    input.addEventListener('input',update);
    keys.forEach(k => controls[k].addEventListener('change',update));
    get('sort').addEventListener('change',render);
    more.addEventListener('click',() => { limit += 24; render(); });
    root.addEventListener('click',e => { if (e.target.closest('button') && !panel.contains(e.target)) setTimeout(update,0); });
    input.addEventListener('input',() => setTimeout(render,150));
    update();
}

class SFaqHub extends HTMLElement {
  connectedCallback() {
    initFaqControls(this);
    initFaqArticles(this);
  }
}

if (!customElements.get('s-faq-hub')) {
  customElements.define('s-faq-hub', SFaqHub);
}
